<template>
  <div class="sheet__bar">
    <div class="sheet__opacity" :style="{ background: props.color }" />
    <div class="sheet__action">
      <div class="sheet__left">
        <BaseText
          size="p5"
          :color="NebraskaColors.elementAlpha"
          class="sheet__label"
        >
          {{ hero?.bottom?.label }}
        </BaseText>
        <BaseText
          :color="NebraskaColors.elementOverImage"
          size="p5"
          class="sheet__value"
        >
          {{ hero?.bottom?.value }}
        </BaseText>
      </div>
      <div class="sheet__buttons">
        <ButtonPrimary
          class="product-side-sheet-action-bar--button"
          v-for="(button, index) in props.hero?.buttons"
          :key="index"
          data-testid="button"
          :disabled="!button?.isActive"
          :icon="props.hero?.warnings?.icon"
          :label="button?.label"
          :loading="isButtonLoading"
          @click="handleClick(props.hero?.buttons?.[index]?.action)"
        />
      </div>
    </div>
    <ContinueRegistrationModal
      :is-open="isContinueRegistrationModalOpen"
      @close="isContinueRegistrationModalOpen = false"
    />
    <ImpersonateEducationalModal
      :is-open="isImpersonateEducationalModalOpen"
      @close="isImpersonateEducationalModalOpen = false"
      @continue="handleContinueAsClient"
    />
  </div>
</template>

<script setup lang="ts">
import {
  EProductTypes,
  IProductHero,
  IProductHeroButtonAction
} from '@/modules/catalog/services/products';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { BaseText, ButtonPrimary } from '@warrenbrasil/nebraska-web';
import { PropType, ref } from 'vue';
import { useRouter } from 'vue-router/composables';
import { ContinueRegistrationModal } from '@/modules/common/components/ContinueRegistrationModal';
import { ImpersonateEducationalModal } from '@/modules/common/components/ImpersonateEducationalModal';
import { isImpersonate } from '@/modules/common/services/partner';

const router = useRouter();

const props = defineProps({
  showHeaderText: Boolean,
  color: {
    type: String,
    default: ''
  },
  hero: {
    type: Object as PropType<IProductHero>,
    default: null
  },
  productApiId: {
    type: String,
    default: ''
  },
  stopInvestment: Boolean,
  categorySelectedProduct: {
    type: String,
    default: ''
  }
});

const emit = defineEmits([
  'open-stop-investment-bar',
  'add-product',
  'remove-product'
]);

const isButtonLoading = ref(false);
const isContinueRegistrationModalOpen = ref(false);
const isImpersonateEducationalModalOpen = ref(false);
const pendingAction = ref<IProductHeroButtonAction | null>(null);

const updateLoading = (value: boolean) => {
  isButtonLoading.value = value;
};

async function handleClick(action: IProductHeroButtonAction) {
  if (props.stopInvestment) {
    emit('open-stop-investment-bar');
    return;
  }

  if (action === IProductHeroButtonAction.ADD) {
    updateLoading(true);
    emit('add-product', props.productApiId);
    return;
  }

  if (action === IProductHeroButtonAction.REMOVE) {
    updateLoading(true);
    emit('remove-product', props.productApiId);
    return;
  }

  if (action === IProductHeroButtonAction.CONTINUE_REGISTRATION) {
    isContinueRegistrationModalOpen.value = true;
    return;
  }

  // Check if user is impersonating before navigating to investment
  if (
    action === IProductHeroButtonAction.INVEST ||
    action === IProductHeroButtonAction.CHOICE
  ) {
    if (isImpersonate()) {
      pendingAction.value = action;
      isImpersonateEducationalModalOpen.value = true;
      return;
    }
  }

  proceedToInvestment(action);
}

function proceedToInvestment(action: IProductHeroButtonAction) {
  const allowedCategories = [
    EProductTypes.FIXED_INCOME,
    EProductTypes.GOVERNMENT_BOND,
    EProductTypes.FUNDS
  ];

  const category = props.categorySelectedProduct as EProductTypes;

  if (allowedCategories.includes(category)) {
    router.push({
      name: 'productInvestment',
      params: {
        type: props.categorySelectedProduct,
        id: props.productApiId
      }
    });
  } else {
    router.push({
      name: 'productInvestmentV2',
      params: {
        type: props.categorySelectedProduct,
        id: props.productApiId
      },
      query:
        action === IProductHeroButtonAction.CHOICE
          ? { selection: 'true' }
          : undefined
    });
  }
}

function handleContinueAsClient() {
  isImpersonateEducationalModalOpen.value = false;
  if (pendingAction.value) {
    proceedToInvestment(pendingAction.value);
    pendingAction.value = null;
  }
}
</script>

<style lang="less" scoped>
.sheet {
  &__action {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: @size-spacing-x400 @size-spacing-x1200;
    z-index: 50;
  }

  &__bar {
    width: 100%;
    position: sticky;
    top: @size-spacing-x1000;
    height: 80px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: @cinza-900;
    z-index: 1;
  }

  .sheet__opacity {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 80px;
    opacity: 0.7;
  }

  &__close {
    position: absolute;
    width: 24px;
    top: @size-spacing-x300;
    right: @size-spacing-x400;
    z-index: 1;

    @media screen and (min-width: 1024px) {
      top: @size-spacing-x450;
      left: @size-spacing-x450;
      right: none;
    }
  }

  &__buttons {
    display: flex;
    gap: @size-spacing-x300;
  }

  &__left {
    display: flex;
    flex-direction: column;
    gap: @size-spacing-x150;
  }

  &__title {
    opacity: 0;
    transition: opacity linear 0.3s;

    &--show {
      opacity: 1;
    }
  }
}

:deep(.product-side-sheet-action-bar--button .button--ui.background) {
  background: rgba(255, 255, 255, 0.15);
  box-shadow: none;
}

:deep(.product-side-sheet-action-bar--button .button--label) {
  color: @element-over-image;
}

:deep(.product-side-sheet-action-bar--button .p5) {
  line-height: 20px;
}
</style>
